import React, { useContext, useId, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useBlastDetail from '../../api/useBlastDetail';
import useSegment from '../../api/useSegment';
import BlockEditor from '../../components/BlockEditor';
import { MarkButton, MarkLabel, MarkSelect } from '../../components/MarkControls';
import { MarkPageHeader, MarkEmptyState } from '../../components/MarkLayout';
import { UnsavedChangesContext } from '../../components/UnsavedChangesProvider';
import * as layout from '../../components/markLayout.module.css';
import * as styles from './template.module.css';
import type { BlastDetail, BlastVariantTarget } from '../../types';

const VariantTemplate: React.FC<{
    blastId: number;
    label: string;
    readOnly: boolean;
    targetKind: string | null;
}> = ({ blastId, label, readOnly, targetKind }) => {
    const target = useMemo<BlastVariantTarget>(
        () => ({ target_type: 'blast_variant', blast_id: blastId, variant_label: label }),
        [blastId, label]
    );
    return (
        <BlockEditor
            target={target}
            channel="email"
            readOnly={readOnly}
            targetKind={targetKind}
            workspace
        />
    );
};

const TemplateWorkspace: React.FC<{ blast: BlastDetail }> = ({ blast }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectorId = useId();
    const { isDirty, anySaving } = useContext(UnsavedChangesContext);
    const requested = new URLSearchParams(location.search).get('variant');
    const selectedLabel =
        blast.variants.find((variant) => variant.label === requested)?.label ??
        blast.variants[0]?.label ??
        '';
    const [mountedVariants, setMountedVariants] = useState(() => new Set([selectedLabel]));
    if (!mountedVariants.has(selectedLabel)) {
        setMountedVariants((current) => new Set([...current, selectedLabel]));
    }
    const segmentQuery = useSegment(blast.segment_id ?? 0);
    const readOnly = blast.status !== 'DRAFT';
    const returnSearch = new URLSearchParams(location.search);
    returnSearch.delete('variant');
    returnSearch.set('tab', 'content');
    const back = () =>
        navigate({ pathname: `/mark/blasts/${blast.blast_id}`, search: returnSearch.toString() });
    const selectVariant = (label: string) => {
        const search = new URLSearchParams(location.search);
        search.set('variant', label);
        navigate({ search: search.toString() });
    };
    return (
        <Section
            subtheme={['regular', 'background']}
            noDefaultPadding
            className={`${layout.page} ${styles.page}`}
        >
            <MarkPageHeader
                title={readOnly ? 'View blast template' : 'Edit blast template'}
                description={blast.name}
            >
                <MarkButton size="regular" disabled={anySaving} onClick={back}>
                    Back to blast
                </MarkButton>
            </MarkPageHeader>
            {blast.variants.length === 0 ? (
                <MarkEmptyState
                    title="No template variants"
                    description="This blast has no variants available to edit."
                />
            ) : (
                <>
                    <div className={styles.variantBar}>
                        <div className={layout.field}>
                            <MarkLabel htmlFor={selectorId}>Template variant</MarkLabel>
                            <MarkSelect
                                id={selectorId}
                                value={selectedLabel}
                                disabled={anySaving}
                                onChange={(event) => selectVariant(event.currentTarget.value)}
                            >
                                {blast.variants.map((variant) => (
                                    <option key={variant.label} value={variant.label}>
                                        {variant.label} ({variant.ratio}%)
                                        {isDirty(
                                            JSON.stringify({
                                                target_type: 'blast_variant',
                                                blast_id: blast.blast_id,
                                                variant_label: variant.label
                                            })
                                        )
                                            ? ' · Unsaved changes'
                                            : ''}
                                    </option>
                                ))}
                            </MarkSelect>
                        </div>
                        <P noDefaultMargin styles={{ color: 'var(--mark-muted)' }}>
                            {readOnly
                                ? `This blast is ${blast.status.toLowerCase()}. Its templates are read-only.`
                                : 'Save each variant separately. Saving a template does not publish or send the blast.'}
                        </P>
                    </div>
                    {segmentQuery.isError && (
                        <div role="alert" className={layout.notice}>
                            <P noDefaultMargin>
                                Audience information could not be loaded.{' '}
                                <MarkButton
                                    disabled={segmentQuery.isFetching}
                                    onClick={() => segmentQuery.refetch()}
                                >
                                    Retry
                                </MarkButton>
                            </P>
                        </div>
                    )}
                    {blast.variants.map(
                        (variant) =>
                            (mountedVariants.has(variant.label) ||
                                variant.label === selectedLabel) && (
                                <div key={variant.label} hidden={variant.label !== selectedLabel}>
                                    <VariantTemplate
                                        blastId={blast.blast_id}
                                        label={variant.label}
                                        readOnly={readOnly}
                                        targetKind={segmentQuery.data?.data?.target_kind ?? null}
                                    />
                                </div>
                            )
                    )}
                </>
            )}
        </Section>
    );
};

const BlastTemplatePage: React.FC = () => {
    const { blastId } = useParams<{ blastId: string }>();
    const numericId = Number(blastId);
    const validId = Number.isInteger(numericId) && numericId > 0;
    const query = useBlastDetail(validId ? numericId : 0);
    if (query.isLoading) return <InlineLoader />;
    if (query.error && !query.data) return <QueryInlineError error={query.error} />;
    const blast = query.data?.data;
    if (!blast)
        return (
            <MarkEmptyState
                title="Blast not found"
                description="The requested blast is unavailable."
            />
        );
    return <TemplateWorkspace key={blast.blast_id} blast={blast} />;
};
export default BlastTemplatePage;
